/**
 * Telling the user the scanner got it.
 *
 * Android vibrates. An iPhone cannot — WebKit ships no Vibration API — so the
 * only channel the platform leaves a web app is sound, and Web Audio there
 * starts suspended and stays suspended until a user gesture resumes it. A
 * context built at the moment of the scan is therefore always born silent: for
 * a while the beep never once played on an iPhone.
 *
 * The context and its unlock now live in `sound/context.ts`, shared with her
 * cues — WebKit unlocks audio on the first gesture of a page and only then, so
 * a second context built later would be permanently suspended. What stays here
 * is the scanner's own voice: two synthesised notes, short enough to land
 * inside the moment of the lock, which is a job no downloaded file can do.
 *
 * Sound still loses to the ring/silent switch on iOS, which is why neither
 * channel is allowed to be the only one: the scanner flashes the frame too.
 */

import { audioContext, primeAudio, unlockAudioOnGesture } from '../sound/context.js'

/** @deprecated Kept as the scanner's name for the shared unlock. */
export const primeScanFeedback = primeAudio
export const unlockScanFeedbackOnGesture = unlockAudioOnGesture

/** Two rising notes, short enough to land inside the moment of the lock. */
const TONES = [
  { hz: 1180, at: 0, length: 0.05 },
  { hz: 1760, at: 0.055, length: 0.075 },
]

/** A lock, announced every way this phone allows. */
export function scanFeedback(): void {
  if (typeof navigator.vibrate === 'function') navigator.vibrate(14)

  const ctx = audioContext()
  if (!ctx) return
  try {
    for (const tone of TONES) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      const at = ctx.currentTime + tone.at
      osc.type = 'sine'
      osc.frequency.value = tone.hz
      // Ramped rather than switched: a square-edged gain change is a click on
      // top of the note, which on a phone speaker is most of what you hear.
      gain.gain.setValueAtTime(0.0001, at)
      gain.gain.exponentialRampToValueAtTime(0.22, at + 0.008)
      gain.gain.exponentialRampToValueAtTime(0.0001, at + tone.length)
      osc.connect(gain).connect(ctx.destination)
      osc.start(at)
      osc.stop(at + tone.length + 0.01)
    }
  } catch {
    // A context the platform has closed or interrupted is not worth reviving
    // in the middle of a scan; the flash still fires.
  }
}
